import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "../../styles/HomeNew.css";
import { PayPalButtons } from "@paypal/react-paypal-js";

export default function Forms() {
  const { id: formId } = useParams();

  const [formDef, setFormDef] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState("");

  const [participantRows, setParticipantRows] = useState([]);
  const [totalFee, setTotalFee] = useState(0);
  const [showPayPal, setShowPayPal] = useState(false);

  const [dynamicDescription, setDynamicDescription] = useState("");

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const res = await fetch(`http://localhost:5000/form?id=${formId}`);
        const json = await res.json();
        setFormDef(json);

        // Initialize form fields
        const initial = {};
        Object.keys(json).forEach(key => {
          const f = json[key];
          if (typeof f === "object" && f.field) {
            initial[f.field] = f.type === "C" ? false : "";
          }
        });
        setFormData(initial);

        // ⭐ Dynamic JS description loader
        if (json.Description && json.Description.endsWith(".js")) {
          import(`./${json.Description}`)
            .then(module => setDynamicDescription(module.default))
            .catch(err => console.error("Error loading description JS:", err));
        }

        // Auto-apply participant count if pre-filled
        if (json["How many participants"]?.value) {
          const count = parseInt(json["How many participants"].value);
          const rows = Array.from({ length: count }, () => ({ name: "", age: "" }));
          setParticipantRows(rows);

          const base = parseFloat(json["Participation Fee"].base || 0);
          const fee = count * base;
          setTotalFee(fee);
          setShowPayPal(fee > 0);

          setFormData(prev => ({
            ...prev,
            participantCount: count
          }));
        }

        setLoading(false);
      } catch (err) {
        console.error("Error loading form:", err);
      }
    };

    fetchForm();
  }, [formId]);

  const handleChange = (field, type, e) => {
    const value = type === "C" ? e.target.checked : e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === "participantCount") {
      const count = parseInt(value || 0);
      const rows = Array.from({ length: count }, () => ({ name: "", age: "" }));
      setParticipantRows(rows);

      const base = parseFloat(formDef["Participation Fee"].base || 0);
      const fee = count * base;
      setTotalFee(fee);
      setShowPayPal(fee > 0);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Find the parent checkbox field
    const parentCheckboxKey = Object.keys(formDef).find(k => formDef[k] && formDef[k].field && k.includes("Are you a parent"));
    const parentCheckboxField = parentCheckboxKey ? formDef[parentCheckboxKey].field : null;
    const isParentChecked = parentCheckboxField ? formData[parentCheckboxField] : false;

    Object.keys(formDef).forEach(key => {
      const f = formDef[key];
      const isParentField = key.includes("Parent First Name") || key.includes("Parent Last Name");

      if (typeof f === "object" && f.required === "y") {
        // Skip parent field validation if parent checkbox is not checked
        if (isParentField && !isParentChecked) {
          return;
        }

        if (f.type === "C" && !formData[f.field]) {
          newErrors[f.field] = "This field is required.";
        }
        if (f.type !== "C" && (formData[f.field] || "").trim() === "") {
          newErrors[f.field] = "This field is required.";
        }
      }
    });

    participantRows.forEach((row, index) => {
      if (!row.name || !row.age) {
        newErrors[`participant_${index}`] = "Name and age are required.";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateFormHTML = () => {
    let html = `<html><head><style>
      body { font-family: Arial, sans-serif; margin: 20px; }
      .form-section { margin-bottom: 30px; }
      .form-field { margin-bottom: 15px; padding: 10px; background-color: #f9f9f9; border-left: 3px solid #4CAF50; }
      .field-label { font-weight: bold; color: #333; margin-bottom: 5px; }
      .field-value { color: #666; padding: 5px 0; }
      .participant-section { margin-top: 20px; padding: 15px; background-color: #f0f0f0; border-radius: 5px; }
      .participant-item { margin-bottom: 10px; padding: 10px; background-color: white; border-radius: 3px; }
      .fee-section { background-color: #e8f5e9; padding: 15px; border-radius: 5px; font-weight: bold; }
    </style></head><body>
    <h2>${formDef.Name}</h2>
    <p><strong>Submitted:</strong> ${new Date().toLocaleString()}</p>
    <div class="form-section">`;

    // Find the parent checkbox field
    const parentCheckboxKey = Object.keys(formDef).find(k => formDef[k] && formDef[k].field && k.includes("Are you a parent"));
    const parentCheckboxField = parentCheckboxKey ? formDef[parentCheckboxKey].field : null;
    const isParentChecked = parentCheckboxField ? formData[parentCheckboxField] : false;

    // Add form fields
    Object.keys(formDef).forEach(key => {
      const f = formDef[key];

      if (key === "Participation Fee" || !typeof f === "object" || !f.field || (f.field === "participantCount" && f.value)) {
        return;
      }

      const isParentField = key.includes("Parent First Name") || key.includes("Parent Last Name");
      if (isParentField && !isParentChecked) {
        return;
      }

      const value = formData[f.field];

      if (f.type === "C") {
        html += `<div class="form-field">
          <div class="field-label">${key}</div>
          <div class="field-value">${f.text || key}<br><strong>${value ? "✓ Accepted" : "✗ Not Accepted"}</strong></div>
        </div>`;
      } else {
        html += `<div class="form-field">
          <div class="field-label">${key}</div>
          <div class="field-value">${value || "(Not provided)"}</div>
        </div>`;
      }
    });

    // Add participant details
    if (participantRows.length > 0) {
      html += `</div><div class="participant-section">
        <h3>Participant Details</h3>`;
      participantRows.forEach((row, index) => {
        html += `<div class="participant-item">
          <strong>Participant ${index + 1}</strong><br>
          Name: ${row.name}<br>
          Age: ${row.age}
        </div>`;
      });
      html += `</div><div class="fee-section">
        Total Participation Fee: $${totalFee}
      </div>`;
    }

    html += `</div></body></html>`;
    //console.log("Generated form HTML:", html);
    return html;
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validateForm()) return;

    const formHTML = generateFormHTML();
    //console.log("formHTML generated:", formHTML.substring(0, 100) + "..."); // Log first 100 chars

    const payload = {
      ID: formDef.ID,
      timestamp: new Date().toISOString(),
      data: {
        ...formData,
        participants: participantRows,
        totalFee,
        formHTML: formHTML  // Move HTML inside data object
      }
    };

    //console.log("Sending payload:", payload); // Log full payload

    await fetch("http://localhost:5000/formsubmit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    setSuccessMsg("Thank you! Your response has been recorded. You may fill another.");

    const cleared = {};
    Object.keys(formDef).forEach(key => {
      const f = formDef[key];
      if (typeof f === "object" && f.field) {
        cleared[f.field] = f.type === "C" ? false : "";
      }
    });

    setFormData(cleared);
    setParticipantRows([]);
    setTotalFee(0);
    setShowPayPal(false);
    setErrors({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) return <p>Loading form…</p>;

  return (
    <div className="form-wrapper">
      {successMsg && <div className="success-banner">{successMsg}</div>}

      <div className="form-header">
        <h2 className="form-title">{formDef.Name}</h2>

        {dynamicDescription ? (
          <div className="form-description">
            <pre style={{ whiteSpace: "pre-wrap" }}>{dynamicDescription}</pre>
          </div>
        ) : (
          formDef.Description && (
            <p className="form-description">{formDef.Description}</p>
          )
        )}
      </div>

      <form onSubmit={handleSubmit} className="dynamic-form">
        {Object.keys(formDef).map(key => {
          const f = formDef[key];

          // Find the parent checkbox field value
          const isParentCheckbox = key.includes("Are you a parent");
          const parentCheckboxField = isParentCheckbox ? f.field :
            Object.keys(formDef).find(k => formDef[k] && formDef[k].field &&
              k.includes("Are you a parent")) ? formDef[Object.keys(formDef).find(k => formDef[k] && formDef[k].field && k.includes("Are you a parent"))].field : null;

          const isParentField = key.includes("Parent First Name") || key.includes("Parent Last Name");
          const isParentChecked = parentCheckboxField ? formData[parentCheckboxField] : false;

          // Hide parent fields if parent checkbox is not checked
          if (isParentField && !isParentChecked) {
            return null;
          }

          if (key === "Participation Fee") {
            return (
              <div key={key} className="form-group">
                <label>
                  Participation Fee — ${formDef["Participation Fee"].base} per participant
                </label>
              </div>
            );
          }

          if (typeof f === "object" && f.field === "participantCount" && f.value) {
            return null;
          }

          if (typeof f === "object" && f.field) {
            return (
              <div key={`${f.field}-${key}`} className="form-group">
                <label>
                  {key}
                  {f.required === "y" && <span style={{ color: "red" }}> *</span>}
                </label>

                {f.type === "I" && (
                  <>
                    <input
                      id={`input-${f.field}`}
                      type="text"
                      value={formData[f.field]}
                      onChange={(e) => handleChange(f.field, f.type, e)}
                    />
                    {f.info && <small className="info-text">{f.info}</small>}
                  </>
                )}

                {f.type === "C" && (
                  <label className="consent-label">
                    <input
                      id={`input-${f.field}`}
                      type="checkbox"
                      checked={formData[f.field]}
                      onChange={(e) => handleChange(f.field, f.type, e)}
                      className="consent-checkbox"
                    />
                    <span className="consent-text">{f.text}</span>
                  </label>
                )}

                {f.type === "D" && (
                  <>
                    <select
                      id={`input-${f.field}`}
                      value={formData[f.field] || ""}
                      onChange={(e) => handleChange(f.field, f.type, e)}
                    >
                      <option value="">-- Select an option --</option>
                      {f.options && Array.isArray(f.options) && f.options.map((opt, index) => (
                        <option key={index} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    {f.info && <small className="info-text">{f.info}</small>}
                  </>
                )}

                {errors[f.field] && (
                  <p className="error-text">{errors[f.field]}</p>
                )}
              </div>
            );
          }

          return null;
        })}

        {participantRows.length > 0 && (
          <div className="participant-section">
            <h3 className="participant-title">Participant Details</h3>
            {participantRows.map((row, index) => (
              <div key={`participant-${index}`} className="participant-row">
                <div className="form-group">
                  <label htmlFor={`participant-name-${index}`}>Participant {index + 1} Name *</label>
                  <input
                    id={`participant-name-${index}`}
                    type="text"
                    value={row.name}
                    onChange={(e) => {
                      const updated = [...participantRows];
                      updated[index].name = e.target.value;
                      setParticipantRows(updated);
                    }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor={`participant-age-${index}`}>Age *</label>
                  <input
                    id={`participant-age-${index}`}
                    type="number"
                    value={row.age}
                    onChange={(e) => {
                      const updated = [...participantRows];
                      updated[index].age = e.target.value;
                      setParticipantRows(updated);
                    }}
                  />
                </div>

                {errors[`participant_${index}`] && (
                  <p className="error-text">{errors[`participant_${index}`]}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {participantRows.length > 0 && (
          <div className="fee-box">
            <p><strong>Total Participation Fee:</strong> ${totalFee}</p>
          </div>
        )}

        {showPayPal ? (
          <div style={{ marginTop: "20px" }}>
            <PayPalButtons
              style={{ layout: "vertical" }}
              createOrder={async () => {
                const valid = validateForm();
                if (!valid) {
                  alert("Please fill all required fields before proceeding to payment.");
                  throw new Error("Form validation failed");
                }
                const res = await fetch("http://localhost:5000/api/orders", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ amount: totalFee }),
                });
                const data = await res.json();
                return data.id;
              }}
              onApprove={async (data) => {
                const res = await fetch(
                  `http://localhost:5000/api/orders/${data.orderID}/capture`,
                  { method: "POST" }
                );
                const captureData = await res.json();
                console.log("Capture result:", captureData);
                handleSubmit({ preventDefault: () => { } });
              }}
              onError={(err) => {
                console.error(err);
                alert("Payment error");
              }}
            />
          </div>
        ) : (
          <button type="submit" className="submit-btn">Submit</button>
        )}
      </form>
    </div>
  );
}
